"use client";

import { motion } from "framer-motion";
import {
  Music,
  CheckCircle2,
  Play,
  User,
  Star,
  MessageSquare,
  Video,
  Users,
  Award,
  Heart,
  TrendingUp,
  Mic,
  Headphones,
  Activity,
  BarChart3,
  ChevronRight,
  Loader2,
  X,
  Upload,
  Trash2,
  Package,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "@/lib/hooks/useAuth";
import toast from "react-hot-toast";
import { uploadMediaFile, uploadRealtimeFeedbackAudio } from "@/lib/api/storage";
import { notifyAdminsNewFeedbackRequest } from "@/lib/api/feedback-notifications";
import { isRealtimeFeedbackPublicEnabled } from "@/lib/config/realtimeFeedbackPublic";
import { requestPortOnePayment } from "@/lib/payments/portone-client";
import {
  getExpertFeedbackDirectApplyMessage,
  isExpertFeedbackDirectApplyEnabled,
} from "@/lib/config/paymentInquiry";
import SlotPicker from "@/components/realtime-feedback/SlotPicker";
import {
  releaseSlot,
  reserveSlot,
  type SlotOption,
} from "@/lib/realtimeBooking/api";
import { createNotification } from "@/lib/api/notifications";
import { FEEDBACK_PRICING, formatWon } from "@/lib/config/feedbackPricing";

// 프로 공개 준비가 끝날 때까지 고객용 프로필과 강사 선택을 숨긴다.
const SHOW_FEEDBACK_INSTRUCTOR_PROFILES = false;

export default function ExpertFeedbackPage() {
  const router = useRouter();
  const { user } = useAuth();
  const [feedbackOption, setFeedbackOption] = useState<1 | 2 | 3>(1); // 1: PDF, 2: 음성, 3: 실시간 1:1
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [successInfo, setSuccessInfo] = useState<{
    requestId: string;
    createdAt: Date;
    productName: string;
    optionLabel: string;
    fileName: string;
    price: number;
  } | null>(null);

  // 카테고리 선택 상태
  const [mainCategory, setMainCategory] = useState<string>("");
  const [instrumentCategory, setInstrumentCategory] = useState<string>("");
  const [specificInstrument, setSpecificInstrument] = useState<string>("");
  const [customInstrument, setCustomInstrument] = useState<string>("");
  const [isGuitarExpanded, setIsGuitarExpanded] = useState<boolean>(false);

  // 파일 업로드 상태
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [audioStoragePath, setAudioStoragePath] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 전문가에게 전달할 메시지 (선택사항)
  const [messageToExpert, setMessageToExpert] = useState<string>("");

  // 실시간 1:1 피드백 예약 슬롯 (옵션 3)
  const [selectedSlot, setSelectedSlot] = useState<SlotOption | null>(null);

  // 선생님 선택 (피드백을 받을 프로)
  interface TeacherCard {
    id: string;
    name: string;
    photoUrl?: string;
    tagline?: string;
    specialty: string[];
    tags: string[];
    experience?: string;
    bio?: string;
    differentiator?: string;
    targetAudience: string[];
    location?: string;
    rating?: number;
    reviewCount?: number;
  }
  const [teachers, setTeachers] = useState<TeacherCard[]>([]);
  const [viewingTeacher, setViewingTeacher] = useState<TeacherCard | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherCard | null>(
    null
  );
  const [realtimeTeacherIds, setRealtimeTeacherIds] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    if (!SHOW_FEEDBACK_INSTRUCTOR_PROFILES) return;

    const loadTeachers = async () => {
      try {
        const { getDb } = await import("@/lib/api/firebase-lazy");
        const { collection, getDocs } = await import("firebase/firestore");
        const db = getDb();
        const snap = await getDocs(collection(db, "instructors"));
        const list: TeacherCard[] = snap.docs
          .map(d => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name || data.nickname || "선생님",
              photoUrl: data.photoUrl || data.profileImage || "",
              tagline: data.tagline || "",
              specialty: Array.isArray(data.specialty)
                ? data.specialty
                : data.specialty
                  ? [data.specialty]
                  : [],
              tags: Array.isArray(data.tags) ? data.tags.slice(0, 3) : [],
              experience: data.experience || "",
              bio: data.bio || "",
              differentiator: data.differentiator || "",
              targetAudience: Array.isArray(data.targetAudience)
                ? data.targetAudience
                : [],
              location: data.location || "",
              rating: typeof data.rating === "number" ? data.rating : undefined,
              reviewCount:
                typeof data.reviewCount === "number" ? data.reviewCount : 0,
            };
          })
          .filter(t => t.name !== "선생님" || t.specialty.length > 0)
          .sort((a, b) => (b.rating || 0) - (a.rating || 0));
        setTeachers(list);

        const { listAvailabilities } = await import(
          "@/lib/realtimeBooking/api"
        );
        const availabilities = await listAvailabilities();
        setRealtimeTeacherIds(
          new Set(availabilities.map(a => a.instructorId))
        );
      } catch (error) {
        console.error("선생님 목록 조회 실패:", error);
      }
    };
    void loadTeachers();
  }, []);

  const openApplyModal = (teacher: TeacherCard | null) => {
    setViewingTeacher(null);
    setSelectedTeacher(teacher);
    // 실시간 예약이 불가한 선생님이면 실시간 옵션 해제
    if (teacher && !realtimeTeacherIds.has(teacher.id)) {
      setFeedbackOption(prev => (prev === 3 ? 1 : prev));
    }
    setShowPaymentModal(true);
  };

  const scrollToTeachers = () => {
    if (!SHOW_FEEDBACK_INSTRUCTOR_PROFILES || teachers.length === 0) {
      // 등록된 선생님이 없으면 기존 방식(관리자 배정)으로 신청
      openApplyModal(null);
      return;
    }
    document
      .getElementById("teacher-select")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // 샘플 보컬 리포트 페이지 이미지 (public 폴더 기준)
  const sampleReportPages = [
    "/[피드백]뮤지컬 루드윅-나의옷_page-0001.jpg",
    "/[피드백]뮤지컬 루드윅-나의옷_page-0002.jpg",
    "/[피드백]뮤지컬 루드윅-나의옷_page-0003.jpg",
    "/[피드백]뮤지컬 루드윅-나의옷_page-0004.jpg",
    "/[피드백]뮤지컬 루드윅-나의옷_page-0005.jpg",
    "/[피드백]뮤지컬 루드윅-나의옷_page-0006.jpg",
  ];
  const [samplePageIndex, setSamplePageIndex] = useState(0);

  const realtimeFeedbackPublic = isRealtimeFeedbackPublicEnabled();

  // 옵션별 정상가 / 할인가
  const feedbackOptions = FEEDBACK_PRICING;

  // 메인 카테고리 (현재는 보컬만 신청 가능)
  const mainCategories = [
    { value: "vocal", label: "보컬", icon: Mic, description: "발성/음정/호흡" },
  ];

  // 악기 카테고리 (연주 선택 시)
  const instrumentCategories = [
    {
      value: "keyboard",
      label: "건반악기",
      instruments: ["피아노", "신디사이저", "오르간"],
    },
    {
      value: "strings",
      label: "현악기",
      guitarSubcategories: [
        "통기타",
        "어쿠스틱 기타",
        "일렉 기타",
        "베이스 기타",
      ],
      instruments: ["우쿨렐레", "바이올린", "비올라", "첼로", "콘트라베이스"],
    },
    {
      value: "winds",
      label: "관악기",
      instruments: [
        "플룻",
        "클라리넷",
        "오보에",
        "바순",
        "색소폰",
        "트럼펫",
        "트롬본",
        "프렌치 호른",
        "튜바",
      ],
    },
    {
      value: "percussion",
      label: "타악기",
      instruments: [
        "드럼",
        "드럼 세트",
        "전자 드럼",
        "팀파니",
        "심벌즈",
        "콩가",
        "봉고",
      ],
    },
    {
      value: "korean",
      label: "국악기",
      instruments: [
        "가야금",
        "거문고",
        "해금",
        "아쟁",
        "대금",
        "피리",
        "단소",
        "장구",
      ],
    },
  ];

  const buildCategoryString = useCallback(() => {
    if (mainCategory === "instrument") {
      if (instrumentCategory === "other") {
        return `악기 연주 - 기타 - ${customInstrument.trim()}`;
      }
      const categoryLabel =
        instrumentCategories.find(c => c.value === instrumentCategory)?.label ||
        "";
      return `악기 연주 - ${categoryLabel} - ${specificInstrument}`;
    }
    return mainCategories.find(c => c.value === mainCategory)?.label || "";
  }, [
    customInstrument,
    instrumentCategory,
    mainCategory,
    specificInstrument,
  ]);

  const selectedSalePrice = feedbackOptions[feedbackOption].salePrice;

  useEffect(() => {
    const option = new URLSearchParams(window.location.search).get("option");
    if (option === "3") {
      setFeedbackOption(3);
    }
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem("nicepay_success");
    if (!raw) return;
    try {
      const saved = JSON.parse(raw) as {
        requestId: string;
        createdAt?: string;
        productName: string;
        optionLabel: string;
        fileName: string;
        price: number;
      };
      sessionStorage.removeItem("nicepay_success");
      if (!saved.requestId) return;
      setSuccessInfo({
        requestId: saved.requestId,
        createdAt: saved.createdAt ? new Date(saved.createdAt) : new Date(),
        productName: saved.productName,
        optionLabel: saved.optionLabel,
        fileName: saved.fileName,
        price: saved.price,
      });
      toast.success("결제가 완료되어 피드백 신청이 접수되었습니다!");
    } catch {
      sessionStorage.removeItem("nicepay_success");
    }
  }, []);

  const formatOrderDate = (date: Date) => {
    const yy = date.getFullYear().toString().slice(-2);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yy}.${mm}.${dd}`;
  };

  const handleSuccessClose = () => {
    setSuccessInfo(null);
  };

  const handleSuccessViewDetail = () => {
    if (!successInfo?.requestId) {
      setSuccessInfo(null);
      router.push("/profile/expert-analysis");
      return;
    }
    setSuccessInfo(null);
    router.push(
      `/profile/expert-analysis?requestId=${encodeURIComponent(successInfo.requestId)}`
    );
  };

  const handleVideoPlay = useCallback(async () => {
    console.log("Play button clicked");
    if (videoRef.current) {
      console.log("Video element found, attempting to play");
      try {
        await videoRef.current.play();
        console.log("Video play successful");
        setIsVideoPlaying(true);
      } catch (error) {
        console.error("Video play error:", error);
        // 자동 재생이 실패하면 사용자에게 수동으로 재생하도록 안내
        setIsVideoPlaying(false);
      }
    } else {
      console.log("Video element not found");
    }
  }, []);

  // 기존 포인트 조회/표시 로직 제거로 간소화
  useEffect(() => {
    if (!showPaymentModal) {
      setFeedbackOption(1); // 모달 닫힐 때 기본값으로 초기화
      setMainCategory("vocal"); // 기본값을 보컬로 고정
      setInstrumentCategory("");
      setSpecificInstrument("");
      setCustomInstrument("");
      setSelectedFile(null);
      setFileUrl(null);
      setUploadProgress(0);
      setMessageToExpert("");
      setIsGuitarExpanded(false);
      setSelectedSlot(null);
      setSelectedTeacher(null);
    }
  }, [showPaymentModal]);

  // 파일 선택 핸들러
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isRealtime = feedbackOption === 3;
    if (isRealtime) {
      const { validateRealtimeFeedbackAudio } = await import(
        "@/lib/utils/fileValidation"
      );
      const validation = validateRealtimeFeedbackAudio(file);
      if (!validation.valid) {
        toast.error(validation.error || "파일 업로드에 실패했습니다.");
        return;
      }
    } else {
      const { validateMediaFile } = await import("@/lib/utils/fileValidation");
      const validation = validateMediaFile(file);
      if (!validation.valid) {
        toast.error(validation.error || "파일 업로드에 실패했습니다.");
        return;
      }
    }

    setSelectedFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    // 파일 업로드
    if (!user) {
      toast.error("로그인이 필요합니다.");
      setIsUploading(false);
      setSelectedFile(null);
      return;
    }

    try {
      console.log("📤 파일 업로드 시작:", {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        uid: user.uid,
      });

      const result = isRealtime
        ? await uploadRealtimeFeedbackAudio(file, user.uid, progress => {
            setUploadProgress(progress.percentage);
          })
        : await uploadMediaFile(file, user.uid, progress => {
            setUploadProgress(progress.percentage);
          });

      console.log("📤 파일 업로드 결과:", result);

      if (result.success && result.url) {
        setFileUrl(result.url);
        setAudioStoragePath(
          "storagePath" in result ? result.storagePath || null : null
        );
        toast.success("파일 업로드 완료!");
        
        // 🎵 AI 음질 자동 검증 (검증 서버가 설정된 경우에만 시도)
        const audioAnalysisUrl = process.env.NEXT_PUBLIC_AUDIO_ANALYSIS_URL;
        if (audioAnalysisUrl) {
          try {
            toast.loading("AI가 음질을 검증하고 있습니다...", { id: "quality-check" });

            const formData = new FormData();
            formData.append("audio", file);

            const qualityResponse = await fetch(`${audioAnalysisUrl}/validate-quality`, {
              method: "POST",
              body: formData,
            });

            const qualityResult = await qualityResponse.json();

            if (qualityResult.isValid) {
              toast.success(`✅ 음질 검증 통과! (${qualityResult.details.duration_seconds}초)`, {
                duration: 4000,
              });
            } else {
              // 검증 실패 - 경고 표시 (업로드는 유지하되 경고)
              toast.error(`⚠️ 음질 문제 감지: ${qualityResult.reason}`, {
                duration: 6000,
              });
              console.warn("음질 검증 실패:", qualityResult);
            }
          } catch (qualityError) {
            console.warn("음질 검증 중 오류 (무시하고 진행):", qualityError);
            // 검증 실패해도 업로드는 유지
          } finally {
            toast.dismiss("quality-check");
          }
        }
      } else {
        console.error("❌ 업로드 실패:", result.error);
        toast.error(result.error || "파일 업로드에 실패했습니다.");
        setSelectedFile(null);
      }
    } catch (error) {
      console.error("❌ 파일 업로드 오류:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "파일 업로드 중 오류가 발생했습니다.";
      toast.error(errorMessage);
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  // 파일 제거 핸들러
  const handleFileRemove = () => {
    setSelectedFile(null);
    setFileUrl(null);
    setAudioStoragePath(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 모달에서 신청/결제 처리. 결제는 나이스페이 직접 연동(아임포트 본인인증 없음).
  const handleExpertFeedbackPayment = async () => {
    const skipPg = isExpertFeedbackDirectApplyEnabled();

    // 로그인 확인
    if (!user) {
      // 로그인 페이지로 먼저 유도
      router.push(
        `/auth/login?callbackUrl=${encodeURIComponent("/expert-feedback")}`
      );
      setShowPaymentModal(false);
      return;
    }

    // 카테고리 검증
    if (!mainCategory) {
      toast.error("분석 카테고리를 선택해주세요.");
      return;
    }

    if (mainCategory === "instrument") {
      if (!instrumentCategory) {
        toast.error("악기 종류를 선택해주세요.");
        return;
      }
      if (instrumentCategory !== "other" && !specificInstrument) {
        toast.error("세부 악기를 선택해주세요.");
        return;
      }
      if (instrumentCategory === "other" && !customInstrument.trim()) {
        toast.error("악기명을 입력해주세요.");
        return;
      }
    }

    if (isUploading) {
      toast.error("파일 업로드가 완료될 때까지 기다려주세요.");
      return;
    }

    // 파일 검증
    if (!fileUrl) {
      toast.error(
        feedbackOption === 3
          ? "피드백용 MP3 음원을 업로드해주세요."
          : "분석할 오디오 또는 비디오 파일을 업로드해주세요."
      );
      return;
    }

    const categoryString = buildCategoryString();
    const optionLabel = feedbackOptions[feedbackOption].label;
    const productTitle = categoryString ? `${categoryString}` : optionLabel;

    setIsProcessing(true);
    let reservedBookingId: string | null = null;

    try {
      const { getDb } = await import("@/lib/api/firebase-lazy");
      const {
        collection,
        doc,
        setDoc,
        updateDoc,
        serverTimestamp,
      } = await import("firebase/firestore");

      const db = getDb();

      const hasVideo = feedbackOption === 3;
      const hasPdf = feedbackOption === 1;

      // 가격 계산 (테스트 기간 무료 / 아니면 할인가)
      const basePrice = feedbackOptions[feedbackOption].price;
      const isPromotionApplied = !skipPg;
      const finalPrice = skipPg
        ? 0
        : feedbackOptions[feedbackOption].salePrice;

      // 전문가 피드백 요청 생성
      const studentName =
        user?.nickname || user?.email?.split("@")[0] || "익명";

      const now = serverTimestamp();
      const isProAssigned = Boolean(
        selectedTeacher || (feedbackOption === 3 && selectedSlot)
      );

      // expert-feedback-requests 컬렉션에만 저장 (중복 방지)
      const feedbackRequestsCollection = collection(db, "expert-feedback-requests");
      const feedbackRequestDocRef = doc(feedbackRequestsCollection);

      const requestData = {
        userId: user.uid,
        userEmail: user.email || "",
        userName: studentName,
        category: categoryString,
        fileUrl: fileUrl,
        fileName: selectedFile?.name || "",
        audioOriginalName: selectedFile?.name || "",
        ...(feedbackOption === 3
          ? {
              audioUrl: fileUrl,
              audioStoragePath: audioStoragePath || null,
              audioFileName: selectedFile?.name || "",
              audioExpiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            }
          : {}),
        messageToExpert: messageToExpert.trim() || "",
        feedbackOption: feedbackOption,
        hasVideo: hasVideo,
        hasPdf: hasPdf,
        productName: productTitle,
        optionLabel,
        originalPrice: basePrice,
        paidAmount: finalPrice,
        // 테스트 무료: 바로 접수(pending). 연동 후: 결제 대기.
        status: skipPg
          ? isProAssigned
            ? ("assigned" as const)
            : ("pending" as const)
          : ("payment_pending" as const),
        paymentStatus: skipPg ? ("paid" as const) : ("pending" as const),
        ...(skipPg
          ? {
              paymentProvider: "manual",
              paymentMethod: "free_test",
              paymentNote: "test_period_free",
              ...(isProAssigned
                ? {
                    assignedAt: now,
                    assignedProResponse: "pending",
                  }
                : {}),
            }
          : {}),
        requestedAt: now,
        createdAt: now,
        updatedAt: now,
        requestType: "pro_feedback",
        sourceCollection: "expert-feedback-requests", // 출처 명시
        promotion: isPromotionApplied
          ? {
              type: "sale_price",
              originalPrice: basePrice,
              finalPrice,
            }
          : null,
        ...(selectedTeacher
          ? {
              assignedProId: selectedTeacher.id,
              assignedProName: selectedTeacher.name,
            }
          : {}),
        ...(feedbackOption === 3 && selectedSlot
          ? {
              scheduledDate: selectedSlot.date,
              scheduledTime: selectedSlot.time,
              assignedProId: selectedSlot.instructorId,
              assignedProName:
                selectedSlot.instructorName || selectedTeacher?.name || "",
            }
          : {}),
      };

      await setDoc(feedbackRequestDocRef, requestData);

      // 실시간 옵션: 결제/접수 전에 슬롯 선점 (동시 예약 방지)
      if (feedbackOption === 3 && selectedSlot) {
        try {
          reservedBookingId = await reserveSlot({
            slot: selectedSlot,
            studentId: user.uid,
            studentName,
            feedbackRequestId: feedbackRequestDocRef.id,
          });
        } catch (slotError) {
          toast.error(
            slotError instanceof Error
              ? slotError.message
              : "선택한 시간 예약에 실패했습니다."
          );
          setSelectedSlot(null);
          return;
        }
      }

      // 테스트 기간: PortOne·본인인증 없이 바로 접수
      if (skipPg) {
        reservedBookingId = null;

        notifyAdminsNewFeedbackRequest({
          requestId: feedbackRequestDocRef.id,
          studentId: user.uid,
          studentName,
          category: categoryString,
          feedbackOption,
          hasVideo,
          hasPdf,
        }).catch(error =>
          console.warn("관리자 피드백 요청 알림 전송 실패:", error)
        );

        if (feedbackOption === 3 && selectedSlot) {
          createNotification({
            userId: selectedSlot.instructorId,
            type: "system_announcement",
            title: "실시간 1:1 피드백 예약",
            message: `${studentName}님이 ${selectedSlot.date.replaceAll("-", ".")} ${selectedSlot.time} 실시간 피드백을 예약했습니다.`,
            link: "/instructor/dashboard",
            priority: "high",
            data: {
              feedbackRequestId: feedbackRequestDocRef.id,
              scheduledDate: selectedSlot.date,
              scheduledTime: selectedSlot.time,
            },
          }).catch(error => console.warn("프로 예약 알림 전송 실패:", error));
        } else if (selectedTeacher) {
          createNotification({
            userId: selectedTeacher.id,
            type: "system_announcement",
            title: "새 피드백 요청",
            message: `${studentName}님이 ${optionLabel}을 신청했습니다.`,
            link: "/instructor/dashboard",
            priority: "high",
            data: {
              feedbackRequestId: feedbackRequestDocRef.id,
            },
          }).catch(error => console.warn("프로 피드백 알림 전송 실패:", error));
        }

        toast.success("피드백 신청이 접수되었습니다!");
        setSuccessInfo({
          requestId: feedbackRequestDocRef.id,
          createdAt: new Date(),
          productName: productTitle,
          optionLabel,
          fileName: selectedFile?.name || "",
          price: finalPrice,
        });
        setShowPaymentModal(false);
        return;
      }

      const merchantUid = feedbackRequestDocRef.id;
      const paymentResult = await requestPortOnePayment({
        merchantUid,
        name: `커넥톤 전문가 피드백 - ${optionLabel}`,
        amount: finalPrice,
        payMethod: "card",
        buyerName: studentName,
        buyerEmail: user.email || undefined,
        buyerTel: user.phoneNumber || undefined,
        pending: {
          kind: "feedback",
          requestId: feedbackRequestDocRef.id,
          reservedBookingId: reservedBookingId || undefined,
          isProAssigned,
          productName: productTitle,
          optionLabel,
          fileName: selectedFile?.name || "",
        },
      });

      if (paymentResult.redirecting) {
        return;
      }

      if (!paymentResult.success || !paymentResult.paymentId) {
        toast.error(paymentResult.errorMessage || "결제가 취소되었습니다.");
        if (reservedBookingId) {
          await releaseSlot(reservedBookingId);
          reservedBookingId = null;
        }
        await updateDoc(feedbackRequestDocRef, {
          updatedAt: serverTimestamp(),
        });
        return;
      }

      const verifyRes = await fetch("/api/payments/portone/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payment_id: paymentResult.paymentId,
          expected_amount: finalPrice,
        }),
      });
      const verifyJson = (await verifyRes.json()) as {
        success?: boolean;
        error?: string;
      };

      if (!verifyRes.ok || !verifyJson.success) {
        toast.error(verifyJson.error || "결제 검증에 실패했습니다.");
        if (reservedBookingId) {
          await releaseSlot(reservedBookingId);
          reservedBookingId = null;
        }
        return;
      }

      await updateDoc(feedbackRequestDocRef, {
        status: isProAssigned ? "assigned" : "pending",
        ...(isProAssigned
          ? {
              assignedAt: serverTimestamp(),
              assignedProResponse: "pending",
            }
          : {}),
        paymentStatus: "paid",
        paymentMethod: paymentResult.payMethod || "portone",
        paymentProvider: paymentResult.pgProvider || "nice",
        paymentVerifiedAt: serverTimestamp(),
        paymentImpUid: paymentResult.impUid || paymentResult.paymentId,
        paymentMerchantUid: paymentResult.paymentId,
        updatedAt: serverTimestamp(),
      });

      // 결제 완료: 예약 확정 (이후 오류가 나도 예약은 유지)
      reservedBookingId = null;

      notifyAdminsNewFeedbackRequest({
        requestId: feedbackRequestDocRef.id,
        studentId: user.uid,
        studentName,
        category: categoryString,
        feedbackOption,
        hasVideo,
        hasPdf,
      }).catch(error =>
        console.warn("관리자 피드백 요청 알림 전송 실패:", error)
      );

      // 담당 프로에게 알림 (실시간은 예약 시각 포함)
      if (feedbackOption === 3 && selectedSlot) {
        createNotification({
          userId: selectedSlot.instructorId,
          type: "system_announcement",
          title: "실시간 1:1 피드백 예약",
          message: `${studentName}님이 ${selectedSlot.date.replaceAll("-", ".")} ${selectedSlot.time} 실시간 피드백을 예약했습니다.`,
          link: "/instructor/dashboard",
          priority: "high",
          data: {
            feedbackRequestId: feedbackRequestDocRef.id,
            scheduledDate: selectedSlot.date,
            scheduledTime: selectedSlot.time,
          },
        }).catch(error => console.warn("프로 예약 알림 전송 실패:", error));
      } else if (selectedTeacher) {
        createNotification({
          userId: selectedTeacher.id,
          type: "system_announcement",
          title: "새 피드백 요청",
          message: `${studentName}님이 ${optionLabel}을 신청했습니다.`,
          link: "/instructor/dashboard",
          priority: "high",
          data: {
            feedbackRequestId: feedbackRequestDocRef.id,
          },
        }).catch(error => console.warn("프로 피드백 알림 전송 실패:", error));
      }

      toast.success("결제가 완료되어 피드백 신청이 접수되었습니다!");
      setSuccessInfo({
        requestId: feedbackRequestDocRef.id,
        createdAt: new Date(),
        productName: productTitle,
        optionLabel,
        fileName: selectedFile?.name || "",
        price: finalPrice,
      });
      setShowPaymentModal(false);
    } catch (error) {
      console.error("전문가 피드백 요청 실패:", error);
      toast.error("요청 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
      if (reservedBookingId) {
        await releaseSlot(reservedBookingId);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // 전문가 데이터
  const experts = [
    {
      id: 1,
      name: "김도현",
      role: "보컬 코치",
      education: "Berklee College of Music 출신",
      experience: "10년 경력",
      rating: 4.9,
      genre: "R&B, Soul, K-POP",
      color: "from-purple-600 to-violet-600",
      videoThumb: "/expert-vocals.jpg",
      comment: "보컬의 감정선이 훌륭하지만, 후렴부 톤을 살리면 더 좋아요.",
    },
    {
      id: 2,
      name: "박서연",
      role: "피아노 강사",
      education: "한국예술종합학교 출신",
      experience: "8년 경력",
      rating: 4.8,
      genre: "클래식, 재즈 피아노",
      color: "from-indigo-600 to-blue-600",
      videoThumb: "/expert-piano.jpg",
      comment: "리듬감이 안정적이고, 페달링 기법이 자연스럽습니다.",
    },
    {
      id: 3,
      name: "이준호",
      role: "프로듀서",
      education: "실용음악과 출신 · 현직",
      experience: "12년 경력",
      rating: 5.0,
      genre: "힙합, 일렉트로닉",
      color: "from-violet-600 to-purple-600",
      videoThumb: "/expert-producer.jpg",
      comment: "믹싱 밸런스가 좋고, 사운드 디자인이 독창적입니다.",
    },
    {
      id: 4,
      name: "최민지",
      role: "작곡가",
      education: "서울대 작곡과 출신",
      experience: "15년 경력",
      rating: 4.9,
      genre: "발라드, OST, 클래식",
      color: "from-pink-600 to-rose-600",
      videoThumb: "/expert-composer.jpg",
      comment: "멜로디 구조가 탄탄하고, 화성 진행이 세련됩니다.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0F0E12] text-white">
      {/* 🎧 Hero Section - 다크 스튜디오 분위기 */}
      <section className="relative pt-12 sm:pt-24 pb-16 sm:pb-20 px-4 overflow-hidden safe-top">
        {/* 배경 그라데이션 */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/20 via-transparent to-transparent" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[120px]" />

        {/* 스튜디오 실루엣 장식 */}
        <div className="absolute top-32 left-10 opacity-5">
          <Mic className="w-32 h-32" />
        </div>
        <div className="absolute bottom-20 right-20 opacity-5">
          <Headphones className="w-40 h-40" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* 좌측 텍스트 */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-4 sm:space-y-6 lg:space-y-8"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="hidden sm:block mb-3"
              >
                {/* 모바일 가독성 우선 배지 */}
              </motion.div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-relaxed">
                <span className="bg-gradient-to-r from-purple-400 via-violet-400 to-indigo-400 bg-clip-text text-transparent">
                  프로의 귀로 직접 듣는
                  <br />
                  진짜 피드백
                </span>
              </h1>

              <p className="text-base sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
                <span className="text-purple-400 font-semibold">
                  &apos;감정&apos;, &apos;표현력&apos;, &apos;디테일&apos;
                </span>
                을 전문가가 잡아드립니다.
                <br className="hidden sm:block" />각 분야별 현직 프로가 직접
                들어보고
                <span className="sm:hidden"> </span>
                <br className="hidden sm:block" />
                음악의 강점과 방향성을 구체적으로 제시합니다.
              </p>

              {/* (임시) 모바일 샘플 영상은 비활성화 */}

              {/* 포인트 블록 */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="flex items-center gap-3 bg-purple-500/10 backdrop-blur-base border border-purple-500/20 rounded-xl px-4 py-3">
                  <Headphones className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold text-base">
                      1:1 청취 피드백
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-purple-500/10 backdrop-blur-base border border-purple-500/20 rounded-xl px-4 py-3">
                  <Music className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold text-base">
                      분야별 전문 분석
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-purple-500/10 backdrop-blur-base border border-purple-500/20 rounded-xl px-4 py-3">
                  <TrendingUp className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold text-base">
                      성장 방향 제안
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={scrollToTeachers}
                  className="group relative px-6 sm:px-8 py-4 bg-gradient-to-r from-purple-600 to-violet-600 rounded-xl font-bold text-base sm:text-lg shadow-[0_0_30px_rgba(168,85,247,0.3)] hover:shadow-[0_0_50px_rgba(168,85,247,0.5)] transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-violet-400 opacity-0 group-hover:opacity-20 transition-opacity" />
                  <div className="relative flex items-center justify-center gap-2">
                    <User className="w-5 h-5" />
                    <div className="flex flex-col items-start sm:items-center leading-tight">
                      <span>보컬 프로 피드백 받기</span>
                      <span className="text-xs sm:text-sm text-purple-100">
                        <span className="line-through mr-1 opacity-80">
                          {formatWon(FEEDBACK_PRICING[1].price)}
                        </span>
                        <span className="font-extrabold text-white">
                          {isExpertFeedbackDirectApplyEnabled()
                            ? "무료"
                            : formatWon(FEEDBACK_PRICING[1].salePrice)}
                        </span>
                      </span>
                    </div>
                  </div>
                </button>

                <Link
                  href="#preview"
                  className="px-6 sm:px-8 py-4 bg-white/5 backdrop-blur-base border border-white/10 text-white rounded-xl font-bold text-base sm:text-lg hover:bg-white/10 hover:border-purple-500/30 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <MessageSquare className="w-5 h-5" />
                  샘플 피드백 보기
                </Link>
              </div>

              {/* 추가 신뢰 문구는 제거 (원래 버전 유지) */}

              {/* 신뢰 지표 */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 pt-6">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span className="text-gray-300 font-semibold text-base sm:text-base">
                    4.9/5.0 평점
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  <span className="text-gray-300 font-semibold text-base sm:text-base">
                    1,200+ 피드백 완료
                  </span>
                </div>
              </div>
            </motion.div>

            {/* (임시) 우측 프로 피드백 샘플 영상은 비활성화 */}
          </div>
        </div>
      </section>

      {/* 📝 피드백 문서 샘플 섹션 */}
      <section id="preview" className="relative py-24 px-4 bg-[#0F0E12]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-6">
              실제 프로{" "}
              <span className="bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                피드백 리포트 예시
              </span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto">
              프로가 실제로 보내주는 리포트가 어떤 구조로 나오는지, 아래 샘플로 확인해볼 수 있어요.
            </p>
          </motion.div>


          {/* 보컬 PDF 리포트 실제 샘플 이미지 + 페이지 넘김 */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-10 max-w-3xl mx-auto bg-gradient-to-br from-gray-900/80 to-gray-800/80 border border-purple-500/20 rounded-3xl p-6 sm:p-8 space-y-6"
          >
            <div className="rounded-2xl bg-white overflow-hidden border border-gray-200">
              <div className="relative w-full max-w-xl mx-auto max-h-[320px] sm:max-h-[380px] overflow-hidden">
                {(() => {
                  const isCoverPage = samplePageIndex === 0;
                  return (
                    <>
                      <Image
                        src={sampleReportPages[samplePageIndex]}
                        alt={`보컬 피드백 PDF 리포트 샘플 페이지 ${samplePageIndex + 1}`}
                        width={1024}
                        height={724}
                        className={`w-full h-auto object-cover ${
                          isCoverPage ? "" : "blur-sm"
                        }`}
                      />
                      {/* 공개 샘플 표지의 개인 곡명은 노출하지 않습니다. */}
                      {isCoverPage && (
                        <div className="pointer-events-none absolute inset-x-0 top-[28%] flex h-[38%] items-center justify-center bg-white px-6 text-center">
                          <div>
                            <p className="text-xs font-semibold tracking-[0.2em] text-purple-500">
                              CONNECTONE
                            </p>
                            <p className="mt-2 text-xl font-bold text-gray-900 sm:text-2xl">
                              보컬 피드백 리포트 샘플
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              개인 정보와 곡명은 비공개 처리되었습니다.
                            </p>
                          </div>
                        </div>
                      )}
                      {/* 표지 이외 페이지의 내용을 자연스럽게 가리는 그라데이션 */}
                      {!isCoverPage && (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white via-white/80 to-transparent" />
                      )}
                    </>
                  );
                })()}
                {/* 페이지 컨트롤 */}
                <div className="absolute bottom-3 left-0 right-0 flex items-center justify-between px-4 sm:px-6">
                  <button
                    type="button"
                    onClick={() =>
                      setSamplePageIndex(prev =>
                        prev === 0 ? sampleReportPages.length - 1 : prev - 1,
                      )
                    }
                    className="px-3 py-1.5 rounded-full text-xs sm:text-sm bg-black/40 text-white border border-white/30 backdrop-blur hover:bg-black/60 transition"
                  >
                    이전 페이지
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] sm:text-xs text-white/80">
                      {samplePageIndex + 1} / {sampleReportPages.length}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSamplePageIndex(prev =>
                        prev === sampleReportPages.length - 1 ? 0 : prev + 1,
                      )
                    }
                    className="px-3 py-1.5 rounded-full text-xs sm:text-sm bg-black/40 text-white border border-white/30 backdrop-blur hover:bg-black/60 transition"
                  >
                    다음 페이지
                  </button>
                </div>
              </div>
            </div>

            {/* 페이지별 구성 설명을 이미지 아래로 이동 */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="text-left">
                <p className="text-xs text-purple-300 font-semibold tracking-[0.16em] uppercase">
                  Vocal Feedback · Sample
                </p>
                <h3 className="text-lg sm:text-xl font-bold text-white mt-1">
                  이 샘플 리포트는 이렇게 구성돼요
                </h3>
                <ul className="mt-3 space-y-1 text-xs sm:text-sm text-gray-300 list-disc list-inside">
                  <li>2페이지: 내 음악 성향 한눈에 보기</li>
                  <li>3페이지: 전체 총평과 지금 상태 정리</li>
                  <li>4페이지: 세부 피드백 (발성/호흡, 톤 등 카테고리별 코멘트)</li>
                  <li>5페이지: 개선 포인트와 추천 연습곡</li>
                  <li>6페이지: 종합 점수와 마무리 정리</li>
                </ul>
              </div>
              <div className="sm:flex items-center text-xs text-gray-400">
                <span className="px-2 py-1 rounded-full border border-gray-700">
                  예시는 실제 리포트를 기반으로 한 샘플 이미지입니다
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-400 text-right">
              실제 리포트는 곡과 상황에 따라 내용이 달라지며, 개인정보는 안전하게 처리됩니다.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 📊 신뢰 섹션 (통계) */}
      <section className="relative py-48 px-4 bg-darkMain">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">
              이미{" "}
              <span className="bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                1,200+ 아티스트
              </span>
              가
              <br />
              성장했습니다.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                value: "98%",
                label: "만족도",
                color: "from-pink-500 to-rose-500",
              },
              {
                icon: TrendingUp,
                value: "+27%",
                label: "평균 성장 점수",
                color: "from-purple-500 to-violet-500",
              },
              {
                icon: Users,
                value: "30+",
                label: "현직 프로 참여",
                color: "from-indigo-500 to-blue-500",
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-base rounded-3xl p-10 border border-purple-500/10 hover:border-purple-500/30 transition-all duration-500 text-center">
                  <div
                    className={`w-20 h-20 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(168,85,247,0.2)]`}
                  >
                    <stat.icon className="w-10 h-10 text-white" />
                  </div>
                  <p className="text-5xl font-extrabold text-white mb-3">
                    {stat.value}
                  </p>
                  <p className="text-xl text-gray-400 font-medium">
                    {stat.label}
                  </p>
                </div>

                {/* 글로우 효과 */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 rounded-3xl blur-xl transition-opacity duration-500`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 💬 (후기 섹션 제거됨 - 보컬 피드백 테스트 단계에서는 노출하지 않음) */}

      {/* 🔥 데이터 vs 전문가 비교 섹션 */}
      <section className="py-24 px-4 bg-[#0F0E12]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">
              데이터 분석을 넘어,{" "}
              <span className="bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                프로의 코칭
              </span>
            </h2>
            <p className="text-xl text-gray-400">
              숫자로는 알 수 없는 음악적 감성과 디테일을 프로가 짚어드립니다.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* AI 리포트 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-indigo-900/30 to-blue-900/30 backdrop-blur-base rounded-3xl p-8 border border-indigo-500/20"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">AI 분석</h3>
                  <p className="text-base text-indigo-300">객관적 데이터 분석</p>
                </div>
              </div>

              <ul className="space-y-3">
                {[
                  "음정 정확도: 82점",
                  "리듬 안정성: 74점",
                  "에너지 레벨: 90점",
                  "템포: 120 BPM",
                  "다이나믹 레인지: 중간",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* 프로 피드백 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-purple-900/30 to-violet-900/30 backdrop-blur-base rounded-3xl p-8 border border-purple-500/20 relative overflow-hidden"
            >
              {/* 글로우 효과 */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl" />

              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      프로 피드백
                    </h3>
                    <p className="text-base text-purple-300">
                      실무 경험 기반 조언
                    </p>
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-base rounded-2xl p-6 space-y-4">
                  <p className="text-gray-200 leading-relaxed">
                    <span className="text-purple-400 font-bold">
                      &quot;음정은 안정적이지만,
                    </span>{" "}
                    후렴에서 감정을 더 실어 &apos;사랑해&apos; 부분에 브레스를
                    추가하면 훨씬 더 감동적일 거예요. 이건 데이터로는 알 수 없는
                    감성적 표현입니다.
                  </p>
                  <p className="text-gray-200 leading-relaxed">
                    <span className="text-purple-400 font-bold">
                      구체적으로,
                    </span>{" "}
                    2절 진입 전 0.5초 정도 여유를 두고, 복식호흡으로 충분한
                    공기를 확보하세요. 그리고 &apos;해&apos; 발음을 살짝 길게
                    끌어주면 완벽합니다! 이런 디테일이 프로와 아마추어의
                    차이입니다.
                  </p>
                  <div className="flex items-center gap-2 text-purple-300 font-semibold pt-2">
                    <Heart className="w-5 h-5 fill-purple-400" />
                    당신의 음악은 이미 충분히 아름답습니다 ✨
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 👩‍🏫 선생님 선택 섹션 */}
      {SHOW_FEEDBACK_INSTRUCTOR_PROFILES && teachers.length > 0 && (
        <section
          id="teacher-select"
          className="relative py-24 px-4 overflow-hidden bg-[#0c0b0f]"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[120px]" />
          <div className="relative max-w-5xl mx-auto space-y-10">
            <div className="text-center space-y-3">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold">
                피드백 받을{" "}
                <span className="bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                  선생님을 선택하세요
                </span>
              </h2>
              <p className="text-gray-400 text-base sm:text-lg">
                선생님을 고른 뒤 문서형 · 음성영상형 · 실시간 중 원하는 방식으로
                신청할 수 있어요
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {teachers.map(teacher => {
                const canRealtime = realtimeTeacherIds.has(teacher.id);
                return (
                  <button
                    type="button"
                    key={teacher.id}
                    onClick={() => setViewingTeacher(teacher)}
                    className="group bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-4 text-left hover:border-purple-500/50 hover:bg-white/[0.07] transition-all"
                  >
                    <div className="flex items-center gap-4">
                      {teacher.photoUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={teacher.photoUrl}
                          alt={teacher.name}
                          className="w-14 h-14 rounded-full object-cover border border-white/20"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center text-white text-xl font-bold">
                          {teacher.name.charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-white font-bold text-lg truncate">
                          {teacher.name}
                        </p>
                        <p className="text-sm text-gray-400 truncate">
                          {teacher.specialty.join(" · ") || "음악 전문가"}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5 min-h-[26px]">
                      {typeof teacher.rating === "number" && teacher.rating > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 text-xs font-semibold">
                          <Star className="w-3 h-3 fill-yellow-300" />
                          {teacher.rating.toFixed(1)}
                        </span>
                      )}
                      {canRealtime && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-300 text-xs font-semibold">
                          <Video className="w-3 h-3" />
                          실시간 예약 가능
                        </span>
                      )}
                      {teacher.tags.map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-white/10 text-gray-300 text-xs"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    {teacher.bio && (
                      <p className="text-sm text-gray-400 line-clamp-2">
                        {teacher.bio}
                      </p>
                    )}

                    <span className="mt-auto w-full py-3 rounded-xl border border-purple-500/40 text-center text-purple-200 font-bold group-hover:bg-purple-500/15 transition-colors">
                      프로필 자세히 보기
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 💜 마무리 CTA 섹션 */}
      <section
        id="experts"
        className="relative py-32 px-4 overflow-hidden bg-[#0F0E12]"
      >
        {/* 배경 글로우 */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-purple-500/10 rounded-full blur-[150px]" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative max-w-5xl mx-auto text-center space-y-10"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-extrabold leading-tight">
            이제, 당신의 음악을
            <br />
            들어줄{" "}
            <span className="bg-gradient-to-r from-purple-400 via-violet-400 to-indigo-400 bg-clip-text text-transparent">
              프로를 만나보세요.
            </span>
          </h2>

          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-300 max-w-3xl mx-auto">
            현직 프로의 귀로 당신의 음악을 듣고,
            <br className="hidden sm:block" />
            구체적인 성장 방향을 제시받으세요.
          </p>

                <div className="flex flex-col sm:flex-row gap-6 justify-center pt-8">
            <button
              onClick={scrollToTeachers}
              className="group relative px-8 sm:px-12 py-4 sm:py-6 bg-gradient-to-r from-purple-600 via-violet-600 to-purple-600 bg-size-200 bg-pos-0 hover:bg-pos-100 rounded-2xl font-bold text-lg sm:text-xl shadow-[0_0_50px_rgba(168,85,247,0.4)] hover:shadow-[0_0_80px_rgba(168,85,247,0.6)] transition-all duration-500 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-violet-400 to-purple-400 opacity-0 group-hover:opacity-30 transition-opacity" />
              <div className="relative flex items-center justify-center gap-3">
                <span className="text-xl">💜</span>
                <div className="flex flex-col items-start sm:items-center leading-tight">
                  <span>보컬 프로 피드백 받기</span>
                  <span className="text-sm text-purple-100">
                    {isExpertFeedbackDirectApplyEnabled() ? (
                      <>
                        <span className="line-through mr-1 opacity-80">
                          {formatWon(FEEDBACK_PRICING[1].price)}
                        </span>
                        <span className="font-extrabold text-white">무료</span>
                      </>
                    ) : (
                      <>
                        <span className="line-through mr-1 opacity-80">
                          {formatWon(FEEDBACK_PRICING[1].price)}
                        </span>
                        <span className="font-extrabold text-white">
                          {formatWon(FEEDBACK_PRICING[1].salePrice)}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </button>
          </div>
          {/* 기존 가격표시/작은 배지/버튼 아래 라벨 등은 삭제! */}

          {/* 신뢰 지표 */}
          <div className="flex flex-wrap justify-center gap-8 pt-12 text-gray-300">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-purple-400" />
              <span className="font-semibold">검증된 프로</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-purple-400" />
              <span className="font-semibold">48시간 내 피드백</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-purple-400" />
              <span className="font-semibold">100% 만족 보장</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* 프로 피드백 결제 모달 */}
      {successInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white text-gray-900 shadow-2xl">
            <button
              onClick={handleSuccessClose}
              className="absolute left-4 top-4 text-gray-400 transition-colors hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="bg-gradient-to-b from-rose-50 via-white to-white px-6 pt-14 pb-10 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-rose-400 to-pink-500 shadow-lg">
                <CheckCircle2 className="h-12 w-12 text-white" />
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900">피드백 신청 완료!</h2>
              <p className="mt-3 text-base leading-relaxed text-gray-500">
                프로에게 전달될 예정입니다.
                <br />
                48시간 이내 요청사항을 반영한 맞춤 리포트를 받아보실 수 있어요.
              </p>
            </div>
            <div className="space-y-6 px-6 pb-7">
              <div className="space-y-4 rounded-2xl border border-gray-100 bg-gray-50 p-5 text-base text-gray-600">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-500">상품번호</span>
                  <span className="font-semibold text-gray-900">
                    No.{successInfo.requestId}
                    <span className="ml-2 text-base text-gray-400">
                      {formatOrderDate(successInfo.createdAt)}
                    </span>
                  </span>
                </div>
                <div className="flex items-start gap-4 rounded-2xl bg-white p-4 shadow-base">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-rose-100">
                    <Package className="h-7 w-7 text-rose-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-semibold uppercase tracking-wide text-rose-400">
                      신청한 피드백
                    </p>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {successInfo.productName}
                    </p>
                    {successInfo.fileName && (
                      <p className="text-base text-gray-500">{successInfo.fileName}</p>
                    )}
                    <p className="text-base text-gray-400">{successInfo.optionLabel}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-semibold uppercase tracking-wide text-gray-400">
                      {successInfo.price === 0 ? "무료 신청" : "결제완료"}
                    </p>
                    <p className="mt-1 text-lg font-bold text-emerald-600">
                      {successInfo.price === 0
                        ? "₩0"
                        : `₩${successInfo.price.toLocaleString()}`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  onClick={handleSuccessViewDetail}
                  className="w-full rounded-xl bg-gray-900 py-3 text-base font-semibold text-white shadow-base transition hover:bg-gray-800"
                >
                  신청내역 상세
                </button>
                <button
                  onClick={handleSuccessClose}
                  className="w-full rounded-xl border border-gray-200 bg-white py-3 text-base font-semibold text-gray-700 transition hover:bg-gray-50"
                >
                  피드백 계속 신청하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 숨고형 선생님 상세 프로필 */}
      {SHOW_FEEDBACK_INSTRUCTOR_PROFILES && viewingTeacher && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-base"
          onClick={e => {
            if (e.target === e.currentTarget) {
              setViewingTeacher(null);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#15131a] shadow-2xl"
          >
            <button
              type="button"
              onClick={() => setViewingTeacher(null)}
              aria-label="프로필 닫기"
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="overflow-y-auto">
              <div className="relative flex min-h-72 items-end justify-center overflow-hidden bg-gradient-to-b from-gray-200 to-gray-300">
                {viewingTeacher.photoUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={viewingTeacher.photoUrl}
                    alt={`${viewingTeacher.name} 프로`}
                    className="max-h-[420px] w-full object-contain object-bottom"
                  />
                ) : (
                  <div className="mb-16 flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-violet-600 text-5xl font-extrabold text-white">
                    {viewingTeacher.name.charAt(0)}
                  </div>
                )}
              </div>

              <div className="space-y-7 px-6 py-7 sm:px-8">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-extrabold text-white sm:text-3xl">
                      {viewingTeacher.name} 프로
                    </h2>
                    {realtimeTeacherIds.has(viewingTeacher.id) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-500/15 px-2.5 py-1 text-xs font-semibold text-green-300">
                        <Video className="h-3.5 w-3.5" />
                        실시간 예약 가능
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-purple-200">
                    {viewingTeacher.tagline ||
                      viewingTeacher.specialty.join(" · ") ||
                      "음악 전문가"}
                  </p>
                  {viewingTeacher.location && (
                    <p className="mt-1 text-sm text-gray-500">
                      활동 지역 · {viewingTeacher.location}
                    </p>
                  )}
                </div>

                {viewingTeacher.bio && (
                  <section>
                    <h3 className="mb-3 text-lg font-bold text-white">프로 소개</h3>
                    <p className="whitespace-pre-line text-sm leading-7 text-gray-300 sm:text-base">
                      {viewingTeacher.bio}
                    </p>
                  </section>
                )}

                {viewingTeacher.differentiator && (
                  <div className="rounded-2xl border border-purple-500/25 bg-purple-500/10 p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-purple-300">
                      코칭 포인트
                    </p>
                    <p className="mt-2 text-sm leading-6 text-purple-100">
                      {viewingTeacher.differentiator}
                    </p>
                  </div>
                )}

                {viewingTeacher.experience && (
                  <section>
                    <h3 className="mb-3 text-lg font-bold text-white">주요 이력</h3>
                    <ul className="space-y-3">
                      {viewingTeacher.experience
                        .split("\n")
                        .filter(Boolean)
                        .map(item => (
                          <li
                            key={item}
                            className="flex gap-3 text-sm leading-6 text-gray-300 sm:text-base"
                          >
                            <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-purple-400" />
                            <span>{item}</span>
                          </li>
                        ))}
                    </ul>
                  </section>
                )}

                <section>
                  <h3 className="mb-3 text-lg font-bold text-white">전문 분야</h3>
                  <div className="flex flex-wrap gap-2">
                    {[...viewingTeacher.specialty, ...viewingTeacher.tags].map(
                      (item, index) => (
                        <span
                          key={`${item}-${index}`}
                          className="rounded-full bg-white/10 px-3 py-1.5 text-sm text-gray-200"
                        >
                          {item}
                        </span>
                      )
                    )}
                  </div>
                </section>

                {viewingTeacher.targetAudience.length > 0 && (
                  <section>
                    <h3 className="mb-3 text-lg font-bold text-white">
                      이런 분께 추천해요
                    </h3>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {viewingTeacher.targetAudience.map(item => (
                        <div
                          key={item}
                          className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2.5 text-sm text-gray-300"
                        >
                          <CheckCircle2 className="h-4 w-4 text-green-400" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>

            <div className="border-t border-white/10 bg-[#15131a]/95 p-4 sm:px-8">
              <button
                type="button"
                onClick={() => openApplyModal(viewingTeacher)}
                className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 py-4 text-base font-bold text-white transition hover:shadow-[0_0_30px_rgba(168,85,247,0.45)]"
              >
                {viewingTeacher.name} 프로에게 피드백 받기
              </button>
              <p className="mt-2 text-center text-xs text-gray-500">
                피드백 형식을 선택한 뒤{" "}
                {isExpertFeedbackDirectApplyEnabled()
                  ? "바로 신청할 수 있어요"
                  : "결제가 진행됩니다"}
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {showPaymentModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-base flex items-center justify-center z-50 p-4"
          onClick={e => {
            if (e.target === e.currentTarget && !isProcessing) {
              setShowPaymentModal(false);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-gradient-to-br from-gray-900 via-purple-900/30 to-gray-900 rounded-3xl max-w-md w-full shadow-2xl border border-purple-500/20 relative max-h-[90vh] flex flex-col"
          >
            {/* 닫기 버튼 */}
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors z-10"
              disabled={isProcessing}
            >
              <X className="w-5 h-5" />
            </button>

            {/* 스크롤 가능한 컨텐츠 영역 */}
            <div className="overflow-y-auto flex-1 px-8 pt-8 pb-4 custom-scrollbar">
              <div className="text-center space-y-6">
                {/* 아이콘 */}
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-violet-500 rounded-2xl flex items-center justify-center mx-auto">
                  <User className="w-10 h-10 text-white" />
                </div>

                {/* 제목 */}
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    프로 피드백 받기
                  </h3>
                  {SHOW_FEEDBACK_INSTRUCTOR_PROFILES && selectedTeacher ? (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/15 border border-purple-500/30">
                      {selectedTeacher.photoUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={selectedTeacher.photoUrl}
                          alt={selectedTeacher.name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs font-bold flex items-center justify-center">
                          {selectedTeacher.name.charAt(0)}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-purple-200">
                        {selectedTeacher.name} 선생님
                      </span>
                    </div>
                  ) : (
                    <p className="text-gray-400">
                      현직 프로가 당신의 음악을 직접 듣고 피드백합니다
                    </p>
                  )}
                </div>

                {/* 카테고리 선택 (보컬 한 가지) */}
                <div className="space-y-4">
                  {/* 메인 카테고리 (보컬만) */}
                  <div className="grid grid-cols-2 gap-3">
                    {mainCategories
                      .filter(category => category.value === "vocal")
                      .map(category => {
                        const Icon = category.icon;
                        return (
                          <button
                            key={category.value}
                            onClick={() => {
                              setMainCategory(category.value);
                              setInstrumentCategory("");
                              setSpecificInstrument("");
                            }}
                            className={`p-4 rounded-xl border-2 transition-all text-left ${
                              mainCategory === category.value
                                ? "border-purple-500 bg-purple-500/20"
                                : "border-gray-700 bg-gray-800/50 hover:border-purple-500/50"
                            }`}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <div
                                className={`p-2 rounded-lg ${
                                  mainCategory === category.value
                                    ? "bg-purple-500/30"
                                    : "bg-gray-700/50"
                                }`}
                              >
                                <Icon
                                  className={`w-5 h-5 ${
                                    mainCategory === category.value
                                      ? "text-purple-400"
                                      : "text-gray-400"
                                  }`}
                                />
                              </div>
                              <div className="flex-1">
                                <div className="text-white font-semibold text-base">
                                  {category.label}
                                </div>
                                <div className="text-base text-gray-400">
                                  {category.description}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                  </div>

                  {/* 악기 연주 선택 시 세부 카테고리 */}
                  {mainCategory === "instrument" && (
                    <div className="space-y-3 pt-2 border-t border-purple-500/20">
                      <h5 className="text-base text-gray-300 font-medium mb-2">
                        악기 종류 선택
                      </h5>

                      {/* 악기 카테고리 */}
                      <div className="space-y-2">
                        {instrumentCategories.map(cat => (
                          <div key={cat.value}>
                            <button
                              onClick={() => {
                                setInstrumentCategory(
                                  instrumentCategory === cat.value
                                    ? ""
                                    : cat.value
                                );
                                if (instrumentCategory !== cat.value) {
                                  setSpecificInstrument("");
                                  setCustomInstrument("");
                                }
                              }}
                              className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                                instrumentCategory === cat.value
                                  ? "border-purple-500 bg-purple-500/20"
                                  : "border-gray-700 bg-gray-800/50 hover:border-purple-500/50"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-white font-medium text-base">
                                  {cat.label}
                                </span>
                                <ChevronRight
                                  className={`w-4 h-4 transition-transform ${
                                    instrumentCategory === cat.value
                                      ? "text-purple-400 rotate-90"
                                      : "text-gray-400"
                                  }`}
                                />
                              </div>
                            </button>

                            {/* 세부 악기 */}
                            {instrumentCategory === cat.value && (
                              <div className="mt-2 pl-4 space-y-2">
                                {/* 악기 목록 그리드 */}
                                <div className="grid grid-cols-2 gap-2 relative">
                                  {/* 현악기의 경우 기타 버튼 포함 */}
                                  {cat.value === "strings" &&
                                    cat.guitarSubcategories && (
                                      <div className="relative">
                                        {(() => {
                                          const selectedGuitar =
                                            cat.guitarSubcategories.find(
                                              g => specificInstrument === g
                                            );
                                          const displayText = selectedGuitar
                                            ? `기타(${selectedGuitar})`
                                            : "기타";

                                          return (
                                            <button
                                              onClick={() => {
                                                setIsGuitarExpanded(
                                                  !isGuitarExpanded
                                                );
                                              }}
                                              className={`p-2 rounded-lg border transition-all text-base flex items-center justify-center relative w-full ${
                                                isGuitarExpanded ||
                                                selectedGuitar
                                                  ? "border-purple-500 bg-purple-500/20 text-purple-300"
                                                  : "border-gray-700 bg-gray-800/30 text-gray-300 hover:border-purple-500/50"
                                              }`}
                                            >
                                              <span className="text-base sm:text-base">
                                                {displayText}
                                              </span>
                                              <ChevronRight
                                                className={`w-3 h-3 absolute right-2 transition-transform ${
                                                  isGuitarExpanded
                                                    ? "rotate-90 text-purple-400"
                                                    : "text-gray-400"
                                                }`}
                                              />
                                            </button>
                                          );
                                        })()}

                                        {/* 기타 서브카테고리 - 모달처럼 위로 떠서 표시 */}
                                        {isGuitarExpanded && (
                                          <div
                                            className="absolute bottom-full left-0 mb-2 z-20 bg-gray-900 border-2 border-purple-500 rounded-lg shadow-2xl overflow-visible"
                                            style={{
                                              width: "max-content",
                                              minWidth: "100%",
                                            }}
                                          >
                                            <div className="text-base font-medium text-gray-300 py-2 px-3 border-b border-purple-500/30 bg-purple-500/10 whitespace-nowrap">
                                              기타 종류
                                            </div>
                                            <div className="flex gap-2 p-2 flex-wrap">
                                              {cat.guitarSubcategories.map(
                                                guitar => (
                                                  <button
                                                    key={guitar}
                                                    onClick={() => {
                                                      setSpecificInstrument(
                                                        guitar
                                                      );
                                                      setCustomInstrument("");
                                                      setIsGuitarExpanded(
                                                        false
                                                      );
                                                    }}
                                                    className={`px-3 py-2 rounded-lg border transition-all text-base whitespace-nowrap ${
                                                      specificInstrument ===
                                                        guitar &&
                                                      !customInstrument
                                                        ? "border-purple-500 bg-purple-500/20 text-purple-300"
                                                        : "border-gray-700 bg-gray-800/50 text-gray-300 hover:border-purple-500/50"
                                                    }`}
                                                  >
                                                    {guitar}
                                                  </button>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                  {/* 일반 악기 목록 */}
                                  {cat.instruments &&
                                    cat.instruments.length > 0 && (
                                      <>
                                        {cat.instruments.map(instrument => (
                                          <button
                                            key={instrument}
                                            onClick={() => {
                                              setSpecificInstrument(instrument);
                                              setCustomInstrument("");
                                            }}
                                            className={`p-2 rounded-lg border transition-all text-base ${
                                              specificInstrument ===
                                                instrument && !customInstrument
                                                ? "border-purple-500 bg-purple-500/20 text-purple-300"
                                                : "border-gray-700 bg-gray-800/30 text-gray-300 hover:border-purple-500/50"
                                            }`}
                                          >
                                            {instrument}
                                          </button>
                                        ))}
                                      </>
                                    )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                        {/* 기타 옵션 */}
                        <div>
                          <button
                            onClick={() => {
                              setInstrumentCategory(
                                instrumentCategory === "other" ? "" : "other"
                              );
                              if (instrumentCategory !== "other") {
                                setSpecificInstrument("");
                                setCustomInstrument("");
                              }
                            }}
                            className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                              instrumentCategory === "other"
                                ? "border-purple-500 bg-purple-500/20"
                                : "border-gray-700 bg-gray-800/50 hover:border-purple-500/50"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-white font-medium text-base">
                                기타
                              </span>
                              <ChevronRight
                                className={`w-4 h-4 transition-transform ${
                                  instrumentCategory === "other"
                                    ? "text-purple-400 rotate-90"
                                    : "text-gray-400"
                                }`}
                              />
                            </div>
                          </button>

                          {/* 직접 입력 필드 */}
                          {instrumentCategory === "other" && (
                            <div className="mt-2 pl-4">
                              <input
                                type="text"
                                value={customInstrument}
                                onChange={e => {
                                  setCustomInstrument(e.target.value);
                                  setSpecificInstrument("");
                                }}
                                placeholder="원하는 서비스를 작성해주세요"
                                className="w-full p-3 bg-gray-800 border-2 border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-colors"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 파일 업로드 */}
                <div className="space-y-3">
                  <h4 className="text-white font-semibold mb-4">
                    {feedbackOption === 3
                      ? "피드백용 음원 업로드"
                      : "분석할 파일 업로드"}
                  </h4>

                  {!fileUrl ? (
                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 hover:border-purple-500/50 transition-all">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={
                          feedbackOption === 3
                            ? "audio/mpeg,audio/mp3,.mp3"
                            : "audio/*,video/*,.m4a,.aac,.3gp"
                        }
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="w-full flex flex-col items-center justify-center space-y-3 py-4 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                            <div className="text-center">
                              <div className="text-base font-medium text-white mb-2">
                                업로드 중... {Math.round(uploadProgress)}%
                              </div>
                              <div className="w-full bg-gray-700 rounded-full h-2">
                                <div
                                  className="bg-gradient-to-r from-purple-500 to-violet-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${uploadProgress}%` }}
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8" />
                            <div className="text-center">
                              <div className="text-base font-medium">
                                {feedbackOption === 3
                                  ? "MP3 음원 선택"
                                  : "오디오 또는 비디오 파일 선택"}
                              </div>
                              <div className="text-base text-gray-500 mt-1">
                                {feedbackOption === 3
                                  ? "MP3만 가능 · 최대 20MB · 30일 보관"
                                  : "MP3, WAV, M4A, AAC, MP4 등 (최대 100MB)"}
                              </div>
                            </div>
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-purple-500/20 border-2 border-purple-500 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                            {selectedFile?.type.startsWith("video/") ? (
                              <Video className="w-5 h-5 text-purple-400" />
                            ) : (
                              <Music className="w-5 h-5 text-purple-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-medium text-base truncate">
                              {selectedFile?.name || "업로드된 파일"}
                            </div>
                            <div className="text-base text-gray-400">
                              {selectedFile
                                ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                                : ""}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={handleFileRemove}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-gray-400 hover:text-red-400"
                          disabled={isUploading}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* 프로에게 전달할 메시지 (선택사항) */}
                <div className="space-y-3">
                  <h4 className="text-white font-semibold mb-2">
                    프로에게 전달할 메시지 (선택사항)
                  </h4>
                  <textarea
                    value={messageToExpert}
                    onChange={e => setMessageToExpert(e.target.value)}
                    placeholder="고민되는 부분이나 특별히 원하는 요청사항을 입력해주세요"
                    className="w-full p-4 bg-gray-800 border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-colors resize-none min-h-[120px] custom-scrollbar"
                    maxLength={1000}
                  />
                  <div className="text-base text-gray-500 text-right">
                    {messageToExpert.length}/1000자
                  </div>
                </div>

                {/* 옵션 선택 */}
                <div className="space-y-3">
                  <h4 className="text-white font-semibold mb-4">
                    피드백 옵션 선택
                  </h4>

                  {/* 옵션 1: PDF 문서 */}
                  <label
                    className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      feedbackOption === 1
                        ? "border-purple-500 bg-purple-500/20"
                        : "border-gray-700 bg-gray-800/50 hover:border-purple-500/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="feedbackOption"
                          value="1"
                          checked={feedbackOption === 1}
                          onChange={() => setFeedbackOption(1)}
                          className="w-5 h-5 text-purple-600"
                        />
                        <div>
                          <div className="text-white font-semibold">
                            문서형 피드백 (PDF)
                          </div>
                          <div className="text-base text-gray-400">
                            상세 분석 리포트
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {isExpertFeedbackDirectApplyEnabled() ? (
                          <div className="text-xl font-bold text-purple-400">
                            무료
                          </div>
                        ) : (
                          <>
                            <div className="text-sm text-gray-500 line-through">
                              ₩{feedbackOptions[1].price.toLocaleString()}
                            </div>
                            <div className="text-xl font-bold text-purple-400">
                              ₩{feedbackOptions[1].salePrice.toLocaleString()}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </label>

                  {/* 옵션 2: 음성 피드백 */}
                  <label
                    className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      feedbackOption === 2
                        ? "border-purple-500 bg-purple-500/20"
                        : "border-gray-700 bg-gray-800/50 hover:border-purple-500/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="feedbackOption"
                          value="2"
                          checked={feedbackOption === 2}
                          onChange={() => setFeedbackOption(2)}
                          className="w-5 h-5 text-purple-600"
                        />
                        <div>
                          <div className="text-white font-semibold">
                            음성·영상형 피드백
                          </div>
                          <div className="text-base text-gray-400">
                            전문가 음성 피드백
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {isExpertFeedbackDirectApplyEnabled() ? (
                          <div className="text-xl font-bold text-purple-400">
                            무료
                          </div>
                        ) : (
                          <>
                            <div className="text-sm text-gray-500 line-through">
                              ₩{feedbackOptions[2].price.toLocaleString()}
                            </div>
                            <div className="text-xl font-bold text-purple-400">
                              ₩{feedbackOptions[2].salePrice.toLocaleString()}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </label>

                  {realtimeFeedbackPublic &&
                  (!selectedTeacher ||
                    realtimeTeacherIds.has(selectedTeacher.id)) ? (
                  <label
                    className={`block p-4 rounded-xl border-2 cursor-pointer transition-all relative ${
                      feedbackOption === 3
                        ? "border-purple-500 bg-purple-500/20"
                        : "border-gray-700 bg-gray-800/50 hover:border-purple-500/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="feedbackOption"
                          value="3"
                          checked={feedbackOption === 3}
                          onChange={() => setFeedbackOption(3)}
                          className="w-5 h-5 text-purple-600"
                        />
                        <div>
                          <div className="text-white font-semibold flex items-center gap-2">
                            실시간 1:1 피드백
                          </div>
                          <div className="text-base text-gray-400">
                            표정과 제스처까지 함께 짚어주는 실시간 1:1 피드백
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {isExpertFeedbackDirectApplyEnabled() ? (
                          <div className="text-xl font-bold text-purple-400">
                            무료
                          </div>
                        ) : (
                          <>
                            <div className="text-sm text-gray-500 line-through">
                              ₩{feedbackOptions[3].price.toLocaleString()}
                            </div>
                            <div className="text-xl font-bold text-purple-400">
                              ₩{feedbackOptions[3].salePrice.toLocaleString()}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </label>
                  ) : null}
                </div>

                {/* 실시간 1:1 피드백 예약 시간 선택 */}
                {feedbackOption === 3 && (
                  <div className="bg-gray-800/50 rounded-2xl p-6 text-left">
                    <h4 className="text-white font-semibold mb-4">
                      날짜와 시간을 선택해 주세요
                    </h4>
                    <SlotPicker
                      selectedSlot={selectedSlot}
                      onSelect={setSelectedSlot}
                      instructorId={selectedTeacher?.id}
                    />
                    {selectedSlot && (
                      <div className="mt-4 p-3 rounded-xl bg-green-500/10 border border-green-500/30 text-sm text-green-300">
                        {selectedSlot.date.replaceAll("-", ".")}{" "}
                        {selectedSlot.time} 예약 · 결제 완료 시 확정됩니다
                      </div>
                    )}
                  </div>
                )}

                {/* 선택된 옵션의 포함 내용 */}
                <div className="bg-gray-800/50 rounded-2xl p-6 text-left space-y-3">
                  <h4 className="text-white font-semibold mb-3">포함 내용:</h4>
                  <div className="space-y-2 text-base text-gray-300">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      <span>1:1 프로 청취 피드백</span>
                    </div>
                    {feedbackOption === 1 && (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                        <span>PDF 상세 분석 리포트</span>
                      </div>
                    )}
                    {feedbackOption === 3 && (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                        <span>실시간 1:1 피드백</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      <span>성장 방향 제안 및 개선 가이드</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      <span>48시간 내 피드백 제공</span>
                    </div>
                  </div>
                </div>

                {/* 총 가격 */}
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold text-lg">
                      {isExpertFeedbackDirectApplyEnabled()
                        ? "신청 금액"
                        : "결제 금액"}
                    </span>
                    <span className="text-4xl font-bold text-green-400">
                      {isExpertFeedbackDirectApplyEnabled()
                        ? "무료"
                        : `₩${selectedSalePrice.toLocaleString()}`}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-400 text-right">
                    {isExpertFeedbackDirectApplyEnabled()
                      ? getExpertFeedbackDirectApplyMessage()
                      : "이 상품은 포인트/적립금 사용이 불가합니다."}
                  </p>
                </div>
              </div>
            </div>

            {/* 고정된 버튼 영역 */}
            <div className="px-8 pb-8 pt-4 border-t border-purple-500/20 bg-gradient-to-br from-gray-900 via-purple-900/30 to-gray-900 rounded-b-3xl">
              <div className="space-y-3">
                <button
                  onClick={handleExpertFeedbackPayment}
                  disabled={isProcessing || !mainCategory}
                  className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      처리 중...
                    </>
                  ) : isExpertFeedbackDirectApplyEnabled() ? (
                    <>{user ? "신청하기" : "로그인 후 신청하기"}</>
                  ) : (
                    <>
                      {user
                        ? `₩${selectedSalePrice.toLocaleString()} 결제하고 신청하기`
                        : "로그인 후 결제하고 신청하기"}
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  disabled={isProcessing}
                  className="w-full py-3 bg-gray-800 text-gray-400 rounded-xl font-semibold hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  나중에 결정하기
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}


